/**
 * An appointment offer's time as the customer reads it in the zone they picked on the When step
 * (JAI-3212), and the summary's date + time pair (CC 0513).
 *
 * The date and the time always come from ONE instant in ONE zone. Before CC 0513 the summary paired
 * the store's civil day (`st.day`) with the time in the picked zone, so a slot that crosses midnight
 * between the two read as a date and a time that never coexist ("Thu, 10/29/2026, 4:00 AM" for
 * Zurich 23:30 Thu seen from Kolkata, which is 04:00 Fri).
 *
 * Never parses a displayed label back into an instant.
 */
import { civilNoon, eventDayRangeLabel, friendlyClock, friendlyDate, friendlyDateNoYear, friendlyDateShort, friendlyTime, tzNameLabel, zonedWallClockIso } from "../../lib/datetime";

export interface ZonedSlot { time?: string; startUtc?: string; endUtc?: string }
export interface ZonedTime {
  /** The time in the workspace's clock, e.g. "4:00 AM". */
  time: string;
  /** `time`, plus "(Fri, Oct 30)" when the zone puts it on another civil day than `dayKey`. */
  label: string;
  /** 24-hour wall clock in the zone ("04:00"), for grouping by part of day. */
  hhmm: string;
  /** The civil day in the zone ("2026-10-30"). */
  dateKey: string;
}

/** `at` on the wall clock of `zone`, or null for an unknown zone. */
function wall(at: Date, zone: string): { key: string; h: number; mi: number } | null {
  try {
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(at).map((x) => [x.type, x.value]));
    return { key: `${p.year}-${p.month}-${p.day}`, h: parseInt(p.hour, 10) % 24, mi: parseInt(p.minute, 10) };
  } catch {
    return null;
  }
}

/** The offer's start (or end, with `end`) in `zone`. Null when there is no zone, no usable instant, or the zone is unknown. */
export function slotTimeInZone(slot: ZonedSlot | null | undefined, zone: string | null | undefined, dayKey?: string, end = false): ZonedTime | null {
  const raw = slot ? (end ? slot.endUtc : slot.startUtc) : "";
  const at = raw ? new Date(raw) : null;
  if (!zone || !at || Number.isNaN(at.getTime())) return null;
  const w = wall(at, zone);
  if (!w) return null;
  const time = friendlyClock(w.h, w.mi);
  return {
    time,
    label: dayKey && w.key !== dayKey ? `${time} (${friendlyDateNoYear(at, zone)})` : time,
    hhmm: `${String(w.h).padStart(2, "0")}:${String(w.mi).padStart(2, "0")}`,
    dateKey: w.key,
  };
}

/**
 * The summary's { date, time } for the picked offer (CC 0513). With a zone picked and a usable instant, both
 * come from that instant in that zone, and the date is that zone's civil day in the workspace's date format.
 * Otherwise the store day with the producer's label (`storeTime`), as before JAI-3212. With no offer picked,
 * the store day and no time.
 */
export function summaryWhenParts(day: string, slot: ZonedSlot | null | undefined, zone: string | null | undefined, storeTime: string): { date: string; time: string; dateKey: string } {
  const z = slot ? slotTimeInZone(slot, zone) : null;
  if (z) return { date: friendlyDateShort(civilNoon(z.dateKey)), time: z.time, dateKey: z.dateKey };
  return { date: day ? friendlyDateShort(civilNoon(day)) : "", time: slot ? storeTime : "", dateKey: day };
}

/* ── The customer's own zone (CC 0515, JAI-3213) ─────────────────────────────────────────────────── */

/** A zone id Intl accepts, else "". */
export function validZone(zone: unknown): string {
  const z = typeof zone === "string" ? zone.trim() : "";
  if (!z) return "";
  try { new Intl.DateTimeFormat("en-US", { timeZone: z }); return z; } catch { return ""; }
}

/** The browser's zone (Intl resolvedOptions), or "" when it reports none. */
export function browserZone(): string {
  try { return validZone(Intl.DateTimeFormat().resolvedOptions().timeZone); } catch { return ""; }
}

/**
 * The zone the page shows times in for the customer, or "" for "as today": the producer's labels in the
 * location's (or event's) own zone.
 *
 * - The setting is off, or the time zone is hidden: "" whatever was picked. Off leaves the page as it was
 *   before the setting existed.
 * - The customer picked a zone: that zone.
 * - Nothing picked yet: the browser's zone when it differs from the location's, else "" (the location's own
 *   labels already are the customer's).
 */
export function customerZone(o: { allow: unknown; showTimezone: unknown; picked?: unknown; browser?: unknown; location?: unknown }): string {
  if (o.allow !== true || o.showTimezone === false) return "";
  const picked = validZone(o.picked);
  if (picked) return picked;
  const browser = validZone(o.browser);
  const location = validZone(o.location);
  return browser && browser !== location ? browser : "";
}

/** "GMT-05:00" for `zone` AT `at` (DST-aware for that date, never today's), "" for an unknown zone. */
export function zoneOffsetLabel(zone: string, at: Date): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longOffset" }).formatToParts(at).find((x) => x.type === "timeZoneName");
    return part ? part.value : "";
  } catch {
    return "";
  }
}

/**
 * The confirmation's When for a booked offer in `zone`: "Thu, 10/29/2026 · 3:00 PM GMT+01:00 (Europe/Zurich)".
 * The time is `slotTimeInZone(...).time`, the very string the slot list printed, so the confirmation shows the
 * digits the customer clicked. Null when the offer has no usable instant or the zone is unknown.
 */
export function confirmWhenInZone(slot: ZonedSlot | null | undefined, zone: string): { date: string; time: string; offset: string; label: string } | null {
  const z = slotTimeInZone(slot, zone);
  if (!z || !slot || !slot.startUtc) return null;
  const date = friendlyDate(civilNoon(z.dateKey));
  const offset = zoneOffsetLabel(zone, new Date(slot.startUtc));
  return { date, time: z.time, offset, label: `${date} · ${z.time} ${offset ? offset + " " : ""}(${zone})` };
}

const ABSOLUTE_INSTANT = /(?:Z|[+-]\d{2}:?\d{2})$/i;
/** An event's stored start/end as a real instant: absolute strings as they are; a naive wall clock read in the
 *  event's own zone. "" when neither works (then the event cannot be re-shown in another zone). */
function eventInstant(raw: unknown, eventZone: string): string {
  const v = typeof raw === "string" ? raw.trim() : "";
  if (!v) return "";
  if (ABSOLUTE_INSTANT.test(v)) return Number.isNaN(new Date(v).getTime()) ? "" : v;
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(v);
  if (!m || !eventZone) return "";
  const iso = zonedWallClockIso(m[1], m[2], eventZone);
  return ABSOLUTE_INSTANT.test(iso) ? iso : "";
}

/**
 * An event card's date, time and "when" line in the customer's zone (CC 0515), in the shape
 * `projectPublicEventToDesignerRow` gives them: the time names the zone in brackets, "9:00 AM (EST)", so the
 * card's "Time zone" switch strips it the same way. Null when `zone` is "", equals the event's own zone, or the
 * event's start cannot be read as an instant; the card then keeps its own projection.
 */
export function eventTimesInZone(row: { start_at?: unknown; end_at?: unknown; timezone?: unknown } | null | undefined, zone: string): { date: string; time: string; when: string } | null {
  if (!row || !zone) return null;
  const eventZone = validZone(row.timezone);
  if (eventZone && eventZone === zone) return null;
  const start = eventInstant(row.start_at, eventZone);
  if (!start) return null;
  const end = eventInstant(row.end_at, eventZone);
  const at = new Date(start);
  const date = eventDayRangeLabel(start, end || null, zone) || friendlyDateShort(at, zone);
  const name = tzNameLabel(zone, at);
  const clock = friendlyTime(at, zone);
  const time = name ? `${clock} (${name})` : clock;
  return { date, time, when: `${date} · ${time}` };
}

/**
 * The `customerTimeZone` a booking or registration sends (CC 0517), or undefined to send none: only with the
 * setting on (and the zone shown), and only when the zone the page used differs from the location's or
 * event's own. A customer who picked the location's own zone sends nothing; one who kept a preselected
 * browser zone that differs sends it.
 */
export function customerTimeZoneForSubmit(o: { allow: unknown; showTimezone: unknown; picked?: unknown; browser?: unknown; location?: unknown }): string | undefined {
  const z = customerZone(o);
  return z && z !== validZone(o.location) ? z : undefined;
}
